import {
  isRecord,
  isValidBoardContent,
  type NewViewBoardPayload,
  readEnvelopeConfiguration,
  readNewViewBoard,
  reportPayloadMismatch,
} from '../components/board/issues/board-envelope'
import type {BoardInitialPayload} from '../components/board/issues/board-payload'
import type {ViewConfiguration} from '../contexts/views/view-configuration'
import {GROUP_BY_PARAM, LAYOUT_PARAM, SORTS_PARAM, SWIMLANE_BY_PARAM} from '../utils/saved-view-url-params'
import type {SavedViewResponse} from '@github-ui/saved-views/queries'

export const SAVED_VIEW_ROUTE_ID = 'savedViewRoute'

const MISMATCH = {routeId: SAVED_VIEW_ROUTE_ID, errorName: 'SavedViewRoutePayloadMismatch'}

/**
 * What a repository saved view page renders: the layout the server served, and with the board on,
 * the stored configuration and the view metadata `/:id.json` answers.
 */
export type SavedViewRoutePayload = ({layout: 'list'} | {layout: 'board'; content: BoardInitialPayload}) & {
  /** Whether this repository offers the board. Off, the page is the list and nothing else is stated. */
  boardEnabled: boolean
  configuration?: ViewConfiguration
  view?: SavedViewResponse
  /** When the board on screen was first fetched. Set when Save copies this response to a clean URL. */
  boardDataUpdatedAt?: number
}

const BOARD_OFF: SavedViewRoutePayload = {boardEnabled: false, layout: 'list'}

/**
 * Reads the route payload the server sent. A board that fails validation is served as the list,
 * which has a query of its own, so the page never draws a board it cannot trust.
 */
export function adaptSavedViewRoutePayload(raw: unknown): SavedViewRoutePayload {
  if (!isRecord(raw) || raw.boardEnabled !== true) return BOARD_OFF

  const configuration = readEnvelopeConfiguration(raw.configuration, MISMATCH)
  const view = readView(raw)
  if (raw.layout === 'board' && isValidBoardContent(raw.content, MISMATCH)) {
    const boardDataUpdatedAt = typeof raw.boardDataUpdatedAt === 'number' ? raw.boardDataUpdatedAt : undefined
    return {boardEnabled: true, layout: 'board', content: raw.content, configuration, view, boardDataUpdatedAt}
  }
  if (raw.layout !== 'list' && raw.layout !== 'board') {
    reportPayloadMismatch(MISMATCH.errorName, `Unrecognised ${SAVED_VIEW_ROUTE_ID} layout: ${String(raw.layout)}`)
  }

  return {boardEnabled: true, layout: 'list', configuration, view}
}

export const SAVED_VIEW_NEW_ROUTE_ID = 'savedViewNewRoute'

const NEW_MISMATCH = {routeId: SAVED_VIEW_NEW_ROUTE_ID, errorName: 'SavedViewNewRoutePayloadMismatch'}

export function adaptSavedViewNewRoutePayload(raw: unknown): NewViewBoardPayload {
  return readNewViewBoard(raw, NEW_MISMATCH)
}

/** The endpoint the page at `pathname` reads its route payload from. */
export function savedViewContentPath(pathname: string): string {
  return `${pathname.replace(/\/$/, '')}/content`
}

/**
 * The URL parameters the server reads to resolve the page. `page` and `visible_fields` stay out:
 * the list pages through its own query, and visible fields never change what the server sends.
 */
export function savedViewRouteSearchParams(searchParams: URLSearchParams): Record<string, string> {
  const params: Record<string, string> = {}
  for (const name of ['q', LAYOUT_PARAM, GROUP_BY_PARAM, SWIMLANE_BY_PARAM, SORTS_PARAM]) {
    const value = searchParams.get(name)
    if (value !== null) params[name] = value
  }
  return params
}

function readView(raw: Record<string, unknown>): SavedViewResponse | undefined {
  const metadata = isRecord(raw.view) ? raw.view : raw
  const savedView = metadata.savedView
  if (
    !isRecord(savedView) ||
    (typeof savedView.id !== 'string' && typeof savedView.id !== 'number') ||
    typeof savedView.name !== 'string' ||
    typeof savedView.query !== 'string' ||
    typeof metadata.viewerCanManageViews !== 'boolean'
  ) {
    return undefined
  }

  // The same metadata `/:id.json` answers, which `savedViewQueryOptions` reads unchecked too.
  return {
    savedView: savedView as unknown as SavedViewResponse['savedView'],
    viewerCanManageViews: metadata.viewerCanManageViews,
  }
}
